/**
 * Deliberate duplicate of the service-desk remote's resolution in
 * `packages/service-desk/src/utils/api/index.ts` (`fetchResolvedConfigurationResult`).
 * `service-desk` does not depend on `@codametrix/ui-common`, so this cannot be shared without a
 * new cross-package dependency. The two must agree — if the remote's precedence changes, change
 * it here too, and see resolve-configuration-value.test.ts.
 */

export interface ConfigurationValueLike {
  tenantId?: string;
  serviceLines?: string[];
  value?: string | null;
  jsonValue?: unknown;
  updatedDate?: number;
  createdDate?: number;
  id?: number;
}

/** The remote's `reverseServiceLineMapper` (packages/service-desk/src/utils/common/index.ts). */
export const CONFIG_SERVICE_LINE_ALIASES: Record<string, string> = {
  'Bedside Pro': 'Inpatient Professional Coding'
};

export const canonicalServiceLine = (serviceLine: string): string =>
  CONFIG_SERVICE_LINE_ALIASES[serviceLine] ?? serviceLine;

const serviceLineMatches = (
  configValue: ConfigurationValueLike,
  serviceLineName: string,
  includeWildcard: boolean = true
): boolean => {
  const serviceLines = configValue.serviceLines || [];
  return (
    serviceLines.includes(serviceLineName) ||
    (includeWildcard && serviceLines.includes('*'))
  );
};

/** jsonValue wins over value, matching the remote. */
const displayValue = (configValue: ConfigurationValueLike): unknown =>
  configValue.jsonValue !== null && configValue.jsonValue !== undefined
    ? configValue.jsonValue
    : configValue.value;

const compareRecency = (
  left: ConfigurationValueLike,
  right: ConfigurationValueLike
): number => {
  const leftKeys = [left.updatedDate ?? 0, left.createdDate ?? 0, left.id ?? 0];
  const rightKeys = [
    right.updatedDate ?? 0,
    right.createdDate ?? 0,
    right.id ?? 0
  ];

  for (let index = 0; index < leftKeys.length; index += 1) {
    if (leftKeys[index] !== rightKeys[index]) {
      return rightKeys[index] - leftKeys[index];
    }
  }

  return 0;
};

/**
 * Returns the winning value as a string, or null when nothing matches.
 * `serviceLine` is canonicalized here, so callers pass the raw active service-line name.
 */
export function resolveConfigurationValue(
  values: ConfigurationValueLike[] | undefined | null,
  tenantId: string,
  serviceLine: string
): string | null {
  if (!Array.isArray(values) || values.length === 0) return null;

  const effectiveServiceLine = canonicalServiceLine(serviceLine);

  const matchCriteria: Array<(v: ConfigurationValueLike) => boolean> = [
    v => v.tenantId === tenantId && serviceLineMatches(v, effectiveServiceLine, false),
    v => v.tenantId === tenantId && !!v.serviceLines?.includes('*'),
    v =>
      v.tenantId !== tenantId &&
      v.tenantId !== '*' &&
      serviceLineMatches(v, effectiveServiceLine, false),
    v =>
      v.tenantId !== tenantId &&
      v.tenantId !== '*' &&
      !!v.serviceLines?.includes('*'),
    v => v.tenantId === '*' && serviceLineMatches(v, effectiveServiceLine, false),
    v => v.tenantId === '*' && !!v.serviceLines?.includes('*')
  ];

  for (const criteria of matchCriteria) {
    const matches = values.filter(criteria);
    if (matches.length > 0) {
      const winner = [...matches].sort(compareRecency)[0];
      const resolved = displayValue(winner);
      return resolved === null || resolved === undefined ? null : String(resolved);
    }
  }

  return null;
}
